// ─── Visionneuse de prise en main à distance (#/remote/{sessionId}) ───
// Rendue SEULE, plein cadre, sans la barre latérale (cf. Dashboard.jsx), dans
// l'onglet ouvert par « Contrôle à distance ». Fermer l'onglet n'arrête PAS la
// session pour les autres techniciens ; « Terminer » l'arrête pour tous, et la
// plateforme la termine d'elle-même 15 s après le départ du DERNIER technicien.

import { useEffect, useRef, useState } from 'react';
import { Loader2, XCircle, CheckCircle2, X, FolderOpen, TerminalSquare, ListChecks, MonitorUp } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { hasPermission } from '../lib/permissions.jsx';
import { Screen } from '../remote/Screen.jsx';
import { Chat } from '../remote/Chat.jsx';
import { Terminal } from '../remote/Terminal.jsx';
import { Files } from '../remote/Files.jsx';
import { TaskManager } from '../remote/TaskManager.jsx';
import { estFinDeSession } from '../remote/protocol.js';

const STEPS = {
  requested: 'Demande envoyée à l’agent (jusqu’à 10 s)…',
  delivered: 'Demande reçue par l’agent, connexion au poste…',
  connecting: 'Connexion au poste…',
  consent: 'En attente de l’accord de l’usager…',
};
const OUTILS = { fichiers: 'Fichiers', terminal: 'Terminal', taches: 'Tâches' };
// `#/remote/{id}?outil=…` : la partie après « ? » n'appartient pas à l'identifiant.
function outilDemande() {
  const q = window.location.hash.split('?')[1] || '';
  const o = new URLSearchParams(q).get('outil');
  return o && OUTILS[o] ? o : '';
}
const FINAL = { refused: 'Accès refusé', failed: 'Connexion impossible', ended: 'Session terminée' };

export function RemoteViewerPage({ sessionId, user, perms }) {
  const [session, setSession] = useState(null);
  const [erreur, setErreur] = useState('');
  const [fin, setFin] = useState('');
  const [panel, setPanel] = useState('');
  const [nonLus, setNonLus] = useState(0);
  const outilInitial = useRef(outilDemande()).current;
  const [onglet, setOnglet] = useState(outilInitial);
  const panelRef = useRef('');
  panelRef.current = panel;

  // Même règle que la plateforme (RemoteSupportRelay) : « prénom nom », sinon nom
  // affiché, sinon courriel. Sinon le technicien se verrait listé parmi « les
  // autres » et ses propres messages compteraient comme non lus.
  const myName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.displayName ||
    user?.email ||
    'Technicien';
  const status = session?.status || '';
  const active = status === 'active';
  const terminal = ['refused', 'failed', 'ended'].includes(status);

  useEffect(() => {
    const base = outilInitial ? OUTILS[onglet || outilInitial] : 'Contrôle à distance';
    document.title = session?.computerName ? base + ' — ' + session.computerName : base;
  }, [session?.computerName, outilInitial, onglet]);

  // Relecture chaque seconde pendant l'établissement, puis toutes les 3 s pour
  // voir la fin décidée ailleurs (autre technicien, usager).
  useEffect(() => {
    if (terminal) return undefined;
    let vivant = true;
    const lire = async () => {
      try {
        const r = await api.fetch('/platform/remote/sessions/' + encodeURIComponent(sessionId));
        if (!vivant) return;
        if (!r.ok) {
          let msg = r.status === 404 ? 'Session introuvable.' : 'Lecture de la session impossible (' + r.status + ').';
          try { const j = await r.json(); if (j?.error) msg = j.error; } catch { /* non JSON */ }
          setErreur(msg);
          return;
        }
        setErreur('');
        setSession(await r.json());
      } catch (e) {
        if (vivant) setErreur('Connexion à la plateforme perdue : ' + (e.message || e));
      }
    };
    lire();
    const iv = setInterval(lire, active ? 3000 : 1000);
    return () => { vivant = false; clearInterval(iv); };
  }, [sessionId, active, terminal]);

  async function terminer() {
    if (!window.confirm('Terminer la session de contrôle à distance ? Elle sera fermée pour tous les techniciens connectés.')) return;
    try {
      const r = await api.fetch('/platform/remote/sessions/' + encodeURIComponent(sessionId) + '/end', { method: 'POST', body: '{}' });
      if (!r.ok) {
        let msg = 'Impossible de terminer la session (' + r.status + ').';
        try { const j = await r.json(); if (j?.error) msg = j.error; } catch { /* non JSON */ }
        alert(msg);
        return;
      }
      setFin('Session terminée.');
    } catch (e) {
      alert('Erreur : ' + (e.message || e));
    }
  }

  const onPanel = (p) => {
    if (p === 'chat') setNonLus(0);
    setPanel((cur) => (cur === p ? '' : p));
  };

  // ── États hors session active ──
  if (fin || terminal) {
    const ok = status === 'ended' || !!fin;
    const titre = fin ? 'Session terminée' : FINAL[status];
    const detail = fin || session?.statusMessage || session?.endReason || '';
    return (
      <Centre>
        {ok ? <CheckCircle2 size={36} className="text-ok" /> : <XCircle size={36} className="text-crit" />}
        <h1 className="text-lg font-bold text-fg">{titre}</h1>
        {detail && <p className="text-sm text-muted text-center max-w-md">{detail}</p>}
        <button onClick={() => window.close()} className="mt-2 bg-rule border border-rule-3 rounded-lg px-4 py-2 text-xs font-semibold text-muted cursor-pointer hover:bg-rule-2">
          Fermer l’onglet
        </button>
      </Centre>
    );
  }

  if (!session) {
    return (
      <Centre>
        {erreur ? <XCircle size={36} className="text-crit" /> : <Loader2 size={32} className="animate-spin text-blue-400" />}
        <p className="text-sm text-muted">{erreur || 'Chargement de la session…'}</p>
      </Centre>
    );
  }

  if (!active) {
    return (
      <Centre>
        <Loader2 size={32} className="animate-spin text-blue-400" />
        <h1 className="text-base font-bold text-fg">{session.computerName}</h1>
        <p className="text-sm text-muted text-center max-w-md" role="status">
          {STEPS[status] || session.statusMessage || 'Établissement de la session…'}
        </p>
        {erreur && <p className="text-xs text-red-300">{erreur}</p>}
      </Centre>
    );
  }

  const caps = session.capabilities || [];
  const canTerminal = hasPermission(perms, 'RunCommands') && caps.includes('terminal');
  const canFiles = hasPermission(perms, 'RunCommands') && caps.includes('fichiers');
  const canTasks = hasPermission(perms, 'RunCommands') && caps.includes('processus');
  const others = (session.technicians || []).filter((t) => t !== myName);
  const who = session.windowsUsername ? session.windowsUsername : 'écran de connexion / console';

  if (outilInitial) {
    const dispo = [
      canFiles && ['fichiers', 'Fichiers', FolderOpen],
      canTerminal && ['terminal', 'Terminal', TerminalSquare],
      canTasks && ['taches', 'Tâches', ListChecks],
    ].filter(Boolean);
    const courant = dispo.some(([k]) => k === onglet) ? onglet : dispo[0]?.[0] || '';
    const voirEcran = () => {
      // Dashboard ne relit pas le hash dans la visionneuse : on recharge sur la vue écran.
      window.location.hash = '#/remote/' + encodeURIComponent(sessionId);
      window.location.reload();
    };
    return (
      <div className="flex flex-col w-full bg-app text-fg" style={{ height: '100vh' }}>
        <div className="flex items-center gap-3 px-3 py-1.5 border-b border-rule bg-card text-xs">
          <span className="font-bold">{session.computerName}</span>
          {session.clientName && <span className="text-muted-2">{session.clientName}</span>}
          <div className="inline-flex rounded-lg border border-rule-3 overflow-hidden ml-2">
            {dispo.map(([k, l, Icone]) => (
              <button
                key={k}
                onClick={() => setOnglet(k)}
                className={`inline-flex items-center gap-1 px-3 py-1 text-xs cursor-pointer border-none ${courant === k ? 'bg-blue-500 text-white font-semibold' : 'bg-card text-muted hover:text-fg'}`}
              >
                <Icone size={14} strokeWidth={1.75} /> {l}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          {caps.includes('ecran') && hasPermission(perms, 'RemoteConnect') && (
            <button onClick={voirEcran} className="inline-flex items-center gap-1 bg-card border border-rule-3 rounded-lg px-2 py-1 text-xs text-fg-2 cursor-pointer hover:text-fg">
              <MonitorUp size={14} strokeWidth={1.75} /> Voir l’écran
            </button>
          )}
          <button onClick={terminer} className="bg-rule border border-rule-3 rounded-lg px-3 py-1 text-xs font-semibold text-muted cursor-pointer hover:bg-rule-2">
            Terminer
          </button>
        </div>
        {/* Panneaux montés une fois (canaux et historique conservés) ; seul l'affichage change. */}
        <div data-clavier-local="" className="flex flex-col flex-1 min-h-0">
          {dispo.length === 0 && <p className="p-4 text-sm text-muted">Aucun outil disponible sur ce poste avec vos permissions.</p>}
          {canFiles && <Files sessionId={sessionId} visible={courant === 'fichiers'} />}
          {canTerminal && <Terminal sessionId={sessionId} visible={courant === 'terminal'} capabilities={caps} />}
          {canTasks && <TaskManager sessionId={sessionId} visible={courant === 'taches'} />}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full bg-app text-fg" style={{ height: '100vh' }}>
      <div className="flex items-center gap-3 px-3 py-1.5 border-b border-rule bg-card text-xs">
        <span className="font-bold">{session.computerName}</span>
        {session.clientName && <span className="text-muted-2">{session.clientName}</span>}
        <span className="text-muted-2">· {who}</span>
        <span className="text-muted-2">
          · {session.unattended ? 'accès non supervisé' : 'accepté par l’usager'}
        </span>
        {others.length > 0 && <span className="text-muted-2">· aussi connecté : {others.join(', ')}</span>}
      </div>
      <div className="flex flex-1 min-h-0">
        <Screen
          sessionId={sessionId}
          bureau={session.windowsSessionId}
          capabilities={caps}
          canTerminal={canTerminal}
          canFiles={canFiles}
          canTasks={canTasks}
          chatUnread={nonLus}
          panel={panel}
          onPanel={onPanel}
          onEnd={terminer}
          onChannelClose={(code) => {
            if (estFinDeSession(code)) setFin(code === 4010 ? 'Session terminée par l’usager du poste.' : 'La session est terminée.');
          }}
        />
        {/* Les deux panneaux restent montés (canaux et historique conservés) ; seul l'affichage change. */}
        <div
          // Panneau latéral : le clavier y reste local (cf. saisieLocale dans Screen.jsx).
          data-clavier-local=""
          className={`${panel ? 'flex' : 'hidden'} flex-col flex-shrink-0 border-l border-rule bg-card`}
          style={{ width: panel === 'taches' ? 640 : panel === 'terminal' || panel === 'fichiers' ? 560 : 340 }}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-rule text-xs font-semibold">
            {panel === 'terminal' ? 'Terminal' : panel === 'fichiers' ? 'Fichiers' : panel === 'taches' ? 'Tâches' : 'Discussion'}
            <button onClick={() => setPanel('')} className="bg-transparent border-none text-muted cursor-pointer" aria-label="Fermer le panneau">
              <X size={14} />
            </button>
          </div>
          <Chat
            sessionId={sessionId}
            bureau={session.windowsSessionId}
            myName={myName}
            visible={panel === 'chat'}
            onIncoming={(auteur) => {
              if (auteur !== myName && panelRef.current !== 'chat') setNonLus((n) => n + 1);
            }}
          />
          {canFiles && <Files sessionId={sessionId} visible={panel === 'fichiers'} />}
          {canTerminal && <Terminal sessionId={sessionId} visible={panel === 'terminal'} capabilities={caps} />}
          {canTasks && <TaskManager sessionId={sessionId} visible={panel === 'taches'} />}
        </div>
      </div>
    </div>
  );
}

function Centre({ children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 w-full bg-app" style={{ minHeight: '100vh' }}>
      {children}
    </div>
  );
}
